/**
 * Availability exceptions as the landlord reads them: consecutive blocked dates with the same note are
 * one "Thu, Nov 26 – Fri, Nov 27" row (a blocked range is saved as one blackout per date); a changed-
 * hours date stays its own row.
 */

import type { AvailabilityException } from '../../../../lib/leasing.api';
import { addDays, exceptionDateKey } from './time-utils';

export interface ExceptionGroup {
  kind: 'blackout' | 'window';
  /** First and last date of the row, inclusive (equal for a single date). */
  from: string;
  to: string;
  /** Ids of the exceptions the row stands for, in date order. */
  ids: string[];
  startTime: string | null;
  endTime: string | null;
  notes: string | null;
}

export function groupExceptions(
  exceptions: readonly Pick<AvailabilityException, 'id' | 'date' | 'kind' | 'startTime' | 'endTime' | 'notes'>[]
): ExceptionGroup[] {
  const sorted = [...exceptions].sort((a, b) => exceptionDateKey(a.date).localeCompare(exceptionDateKey(b.date)));
  const groups: ExceptionGroup[] = [];
  for (const ex of sorted) {
    const date = exceptionDateKey(ex.date);
    const last = groups[groups.length - 1];
    if (ex.kind === 'blackout' && last && last.kind === 'blackout' && last.notes === ex.notes && addDays(last.to, 1) === date) {
      last.to = date;
      last.ids.push(ex.id);
      continue;
    }
    groups.push({
      kind: ex.kind,
      from: date,
      to: date,
      ids: [ex.id],
      startTime: ex.startTime,
      endTime: ex.endTime,
      notes: ex.notes,
    });
  }
  return groups;
}
